import React, { Fragment, useMemo } from 'react';
import { TableWrapper, Header, Table } from '../Styles/SummaryPortfolioModelStyles';
import { ThemeContext } from '@fluentui/react-theme-provider';
import { useContext, useEffect, useState } from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { setCurrency } from '@/utils/numberFormatter';
import IroniaPoints from '../../../Portfolios/Summary/Components/IroniaPoints';
import { ChevronDownIcon, ChevronUpIcon } from "@fluentui/react-icons-mdl2";
import { ReactComponent as IncrementIcon } from '@/images/increment.svg';
import { ReactComponent as DecrementIcon } from '@/images/decrement.svg';
import { Link } from 'react-router-dom';
import { IPortfolioModelTable } from '../Interfaces/SummaryPortfolioInterfaces';
import { TooltipHost } from '@fluentui/react';
import { InfoIcon } from '@fluentui/react-icons-mdl2';

const PortfolioModelTable = ({data, isLatestComposition, isLoading, currentDate = undefined, showPortfolioColumn = false}: {data: IPortfolioModelTable[], isLatestComposition: boolean, isLoading: boolean, currentDate?: string, showPortfolioColumn?: boolean}) =>
{
    const theme = useContext(ThemeContext);
    const [expandedPortfolios, setExpandedPortfolios] = useState<{[key: string]: boolean}>({});
    // Inicializar todas las carteras como expandidas cuando cambian los datos
    useEffect(() =>
    {
        if(showPortfolioColumn && data && data.length > 0)
        {
            const portfolioNames = Array.from(new Set(data.map(item => item.portfolioName).filter(Boolean)));
            const initialExpanded: {[key: string]: boolean} = {};
            portfolioNames.forEach(name =>
            {
                if (name) initialExpanded[name] = true;
            });
            setExpandedPortfolios(initialExpanded);
        }
    }, [data, showPortfolioColumn]);

    const togglePortfolio = (portfolioName: string) =>
    {
        setExpandedPortfolios(prev =>
        ({
            ...prev,
            [portfolioName]: !prev[portfolioName]
        }));
    };

    // Memoizar si debe mostrar el icono para evitar re-renders innecesarios
    const shouldShowIcon = useMemo(() => !isLatestComposition && !isLoading && data && data.length > 0, [isLatestComposition, isLoading, data]);

    // Si está cargando, mostrar siempre el estado de carga independientemente de si hay datos
    if(isLoading)
    {
        return(
            <React.Fragment>
                <TableWrapper theme={theme}>
                    <Header>
                        {showPortfolioColumn ? <h3>Distribución de las Carteras Modelo seleccionadas</h3> : 
                        (
                            isLatestComposition ? 
                            <h3>Distribución de tu Cartera Modelo</h3> : 
                            <CompositionTitle title={`Distribución de tu Composición${currentDate ? ` - ${currentDate}` : ''}`} shouldShowIcon={false} theme={theme} />
                        )}
                    </Header>
                    <CompositionLoadingState theme={theme} isLatestComposition={isLatestComposition} />
                </TableWrapper>
            </React.Fragment>
        );
    }

    if(!data || data.length === 0)
    {
        return(
            <React.Fragment>
                <TableWrapper theme={theme}>
                    <Header>
                        {isLatestComposition ? 
                            <h3>Distribución de tu Cartera Modelo</h3> : 
                            <CompositionTitle title="Distribución de tu Composición" shouldShowIcon={shouldShowIcon} theme={theme} />
                        }
                    </Header>
                    <p style={{cursor: 'default'}}>Selecciona al menos una Cartera Modelo para ver sus datos</p>
                </TableWrapper>
            </React.Fragment>
        );
    }

    if(showPortfolioColumn)
    {
        const groupedData = data.reduce((acc, item) =>
        {
            const portfolioName = item.portfolioName || 'Sin nombre';
            if(!acc[portfolioName])
            {
                acc[portfolioName] = [];
            }
            acc[portfolioName].push(item);
            return acc;
        }, {} as {[key: string]: IPortfolioModelTable[]});

        const hasMultiplePortfolios = Object.keys(groupedData).length > 1;

        return(
            <React.Fragment>
                <TableWrapper theme={theme}>
                    <Header>
                        {
                            showPortfolioColumn ? <h3>Distribución de las Carteras Modelo seleccionadas</h3> : 
                            (isLatestComposition ? 
                                <h3>Distribución de tu Cartera Modelo</h3> : 
                                <CompositionTitle title={`Distribución de tu Composición${currentDate ? ` - ${currentDate}` : ''}`} shouldShowIcon={shouldShowIcon} theme={theme} />
                            )
                        }
                    </Header>
                    <Table theme={theme} $hasMultiplePortfolios={hasMultiplePortfolios}>
                        <thead>
                            <tr>
                                <th>Cartera Modelo / Nombre del Fondo</th>
                                <th>ISIN</th>
                                <th>Porcentaje en la Cartera</th>
                                <th>RTB 1 día</th>
                                <th>RTB YTD</th>
                                <th>Vola 1Y</th>
                                <th>Valor Liquidativo</th>
                                <th>Fecha de Valor Liquidativo</th>
                                <th>Divisa</th>
                                <th>Puntos IronIA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedData).map(([portfolioName, funds]) =>
                            (
                                <Fragment key={portfolioName}>

                                    <tr style={
                                        { 
                                            backgroundColor: theme?.palette?.neutralLighter || '#f8f9fa',
                                            cursor: 'pointer',
                                            fontWeight: 'bold'
                                        }}
                                        onClick={() => togglePortfolio(portfolioName)}
                                    >
                                        <td style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            {expandedPortfolios[portfolioName] ? 
                                                <ChevronUpIcon style={{ fontSize: '14px' }} /> : 
                                                <ChevronDownIcon style={{ fontSize: '14px' }} />
                                            }
                                            <span>{portfolioName}</span>
                                            <span>({funds.length} fondos)</span>
                                        </td>
                                        <td colSpan={9}></td>
                                    </tr>
                                    {expandedPortfolios[portfolioName] && funds.map((fund, index) =>
                                    (
                                        <tr key={`${portfolioName}-${index}`}>
                                            <td style={{ paddingLeft: '2rem' }}><Link to={`/${fund.isin}`}>{fund.nameFund}</Link></td>
                                            <td>{fund.isin}</td>
                                            <td>{fund.portfolioPercentage.toFixed(2).replace('.', ',')}%</td>
                                            <td>
                                                {fund.oneDayLinearReturn !== undefined && fund.oneDayLinearReturn !== null ?
                                                    (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                                            <span>{fund.oneDayLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                                            {fund.oneDayLinearReturn < 0 && <DecrementIcon />}
                                                            {fund.oneDayLinearReturn > 0 && <IncrementIcon />}
                                                        </div>
                                                    )
                                                    : '-'}
                                            </td>
                                            <td>{fund.ytdLinearReturn !== undefined ?
                                                (
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                                        <span>{fund.ytdLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                                        {fund.ytdLinearReturn < 0 && <DecrementIcon />}
                                                        {fund.ytdLinearReturn > 0 && <IncrementIcon />}
                                                    </div>
                                                )
                                                : '-'}
                                             </td>
                                            <td>{fund.oneYearVolatility !== undefined ? `${fund.oneYearVolatility.toFixed(2).replace('.', ',')}%` : '-'}</td>
                                            <td>{setCurrency('EUR', fund.fundPrice)}</td>
                                            <td>{fund.liquidationDate}</td>
                                            <td>{fund.currency}</td>
                                            <td><IroniaPoints score={fund.ironiaPoints} /></td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </Table>
                </TableWrapper>
            </React.Fragment>
        );
    }
    
    return(
        <React.Fragment>
            <TableWrapper theme={theme}>
                <Header>
                    {
                        isLatestComposition ? 
                        <h3>Distribución de tu Cartera Modelo</h3> : 
                        <CompositionTitle title={`Distribución de tu Composición${currentDate ? ` - ${currentDate}` : ''}`} shouldShowIcon={shouldShowIcon} theme={theme} />
                    }
                </Header>
                <Table theme={theme}>
                    <thead>
                        <tr>
                            <th>Nombre del Fondo</th>
                            <th>ISIN</th>
                            <th>Porcentaje en la Cartera</th>
                            <th>RTB 1 Día</th>
                            <th>RTB YTD</th>
                            <th>Vola 1Y</th>
                            <th>Valor Liquidativo</th>
                            <th>Fecha de Valor Liquidativo</th>
                            <th>Divisa</th>
                            <th>Puntos IronIA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) =>
                        (
                            <tr key={index}>
                                <td><Link to={`/${item.isin}`}>{item.nameFund}</Link></td>
                                <td>{item.isin}</td>
                                <td>{item.portfolioPercentage.toFixed(2).replace('.', ',')}%</td>
                                <td>
                                    {item.oneDayLinearReturn !== undefined && item.oneDayLinearReturn !== null ?
                                    (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                            <span>{item.oneDayLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                            {item.oneDayLinearReturn < 0 && <DecrementIcon />}
                                            {item.oneDayLinearReturn > 0 && <IncrementIcon />}
                                        </div>
                                    )
                                    : '-'}
                                </td>
                                <td>{item.ytdLinearReturn !== undefined ?
                                    (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                            <span>{item.ytdLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                            {item.ytdLinearReturn < 0 && <DecrementIcon />}
                                            {item.ytdLinearReturn > 0 && <IncrementIcon />}
                                        </div>
                                    )
                                    : '-'}
                                </td>
                                <td>{item.oneYearVolatility !== undefined ? `${item.oneYearVolatility.toFixed(2).replace('.', ',')}%` : '-'}</td>
                                <td>{setCurrency('EUR',item.fundPrice)}</td>
                                <td>{item.liquidationDate}</td>
                                <td>{item.currency}</td>
                                <td><IroniaPoints score={item.ironiaPoints} /></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableWrapper>
        </React.Fragment>
    );
}

// Componente separado para el título con icono de información y tooltip
const CompositionTitle = React.memo(({title, shouldShowIcon, theme}: {title: string, shouldShowIcon: boolean, theme: any}) =>
{
    return(
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <h3 style={{margin: 0}}>{title}</h3>
            {shouldShowIcon &&
            (
                <TooltipHost content="Los datos mostrados de cada fondo corresponden a los valores actuales, no a los valores que tenían en la fecha de esta composición histórica."
                    styles={{root: {display: 'inline-flex', alignItems: 'center'}}}
                >
                    <InfoIcon style={{fontSize: '16px', cursor: 'pointer', color: theme?.palette?.themePrimary || '#0078d4'}} />
                </TooltipHost>
            )}
        </div>
    );
});

// Componente para mostrar el estado de carga de composiciones
const CompositionLoadingState = React.memo(({theme, isLatestComposition}: {theme: any, isLatestComposition?: boolean}) =>
{
    const loadingText = isLatestComposition ? "Estamos recuperando la información completa de la Cartera Modelo..." : "Estamos recuperando la información completa de la Composición para esta fecha...";

    return(
        <div style={
        {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem 2rem',
            gap: '1.5rem',
            minHeight: '200px'
        }}>
            <Spinner size={SpinnerSize.large} 
                styles={
                {
                    circle: 
                    {
                        borderTopColor: theme?.palette?.themePrimary || '#CC214F'
                    }
                }}
            />
            <p style={
            {
                fontSize: '16px',
                color: theme?.palette?.neutralPrimary || '#323130',
                textAlign: 'center',
                margin: 0,
                fontWeight: '500',
                cursor: 'default'
            }}>
                {loadingText}
            </p>
        </div>
    );
});


export default PortfolioModelTable;